// The design lock: the rules of play that are choices, not laws. Changing
// any value here changes the game, so tests/unit/config.test.ts asks for a
// version bump and a history entry (src/config/history.ts) when it does.

import type { RiteId } from "../domain/rite.ts"

export type RungConfig = { readonly cards: number; readonly cols: number; readonly altars: number }

export type RulesConfig = {
  // the guided start: the first rite, and the only one its win opens
  readonly firstRite: RiteId
  readonly secondRite: RiteId
  // wins needed in one layer of the lattice before the next opens: the
  // whole tablet before, so a tablet folds and the next one opens
  readonly sinkNeedsStillWins: number
  readonly riseNeedsSinkWins: number
  // The ladder: how big a rite is. A deck opens a third at a time (a line
  // of the plane, then a plane of the cube), and each rung gives the table
  // one more place: 3 cards on 3 places, 9 on 6, the whole cube of 27 on 9.
  readonly ladder: readonly RungConfig[]
  // A rite's rung (src/domain/shape.ts): where its tablet starts, in the
  // order the tablets open, plus how far the rite sits from the tablet's
  // middle. So a place on the next tablet is one rung higher.
  readonly tabletRung: { readonly still: number; readonly sink: number; readonly rise: number }
  readonly placeRung: { readonly middle: number; readonly beside: number; readonly aboveBelow: number; readonly corner: number }
  // a dealt rite is chosen to take this many moves: par = (cards - 3) × 3/2,
  // so 7 cards take 6, 9 take 9, 15 take 18 and 21 take 27
  readonly parPerCard: number
  // the first rite shows a free ghost move after this long idle
  readonly idleGhostMs: number
  // what each kind of help costs; a rite won with none is "unaided". The
  // augury is the Glimmer pressed twice: the next move on a way to the Monad.
  readonly aid: { readonly glimmer: number; readonly augury: number; readonly rosetta: number }
  // how hard the solver may think before giving up
  readonly solver: {
    readonly wayNodes: number // the augury, looking ahead from the table
    readonly wayBackNodes: number // and, on a dead end, from each table before it
    readonly wayBackMoves: number // how many moves back it looks
    readonly deadEndNodes: number
  }
  // the move counter: five alien digits, so at most 121
  readonly counterDigits: number
  readonly counterMax: number
}

export const RULES = {
  firstRite: "000",
  secondRite: "+00",
  sinkNeedsStillWins: 9,
  riseNeedsSinkWins: 9,
  ladder: [
    { cards: 3, cols: 3, altars: 0 },
    { cards: 5, cols: 3, altars: 1 },
    { cards: 7, cols: 4, altars: 1 },
    { cards: 9, cols: 5, altars: 1 },
    { cards: 15, cols: 5, altars: 2 },
    { cards: 21, cols: 6, altars: 2 },
    { cards: 27, cols: 7, altars: 2 },
  ],
  tabletRung: { still: 0, sink: 1, rise: 2 },
  placeRung: { middle: 0, beside: 1, aboveBelow: 2, corner: 3 },
  parPerCard: 1.5,
  idleGhostMs: 25_000,
  aid: { glimmer: 1, augury: 2, rosetta: 3 },
  solver: { wayNodes: 120_000, wayBackNodes: 40_000, wayBackMoves: 30, deadEndNodes: 40_000 },
  counterDigits: 5,
  counterMax: 121,
} as const satisfies RulesConfig
